import { Disclosure, Menu, Transition } from "@headlessui/react";
import {
  ChevronDownIcon,
  MenuIcon,
  XIcon,
} from "@heroicons/react/outline";

import { Button } from "../ui";
import EasyTrans from "./EasyTrans";
import { Fragment } from "react";
import { NavLink } from "react-router-dom";
import React from "react";
import UserService from "../../services/UserService";
import fairkomlogo from "./../../../src/assets/logo/fairregister_logo.png";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function initials(name) {
  if (!name) return "?";
  return name
    .trim()
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

// Single source of truth for the primary nav. Active state comes from the
// router (NavLink isActive) — no manual route tracking.
const NAV_ITEMS = [
  { to: "/", end: true, label: "Navigation.Landingpage" },
  { to: "/searchworks", label: "Navigation.Worksearch" },
  { to: "/registerwork", label: "Navigation.Workregister" },
  { to: "/myworks", label: "Navigation.MyWorks" },
];

const navLinkClass = ({ isActive }) =>
  classNames(
    "rounded-md px-3 py-2 text-sm font-medium transition-colors",
    isActive
      ? "bg-fairblue-100 text-fairblue-700"
      : "text-gray-600 hover:bg-fairgray hover:text-fairblue-700",
  );

const mobileLinkClass = ({ isActive }) =>
  classNames(
    "block rounded-md px-3 py-2 text-base font-medium",
    isActive
      ? "bg-fairblue-100 text-fairblue-700"
      : "text-gray-700 hover:bg-fairgray hover:text-fairblue-700",
  );

const Navbar = () => {
  return (
    <Disclosure
      as="nav"
      className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-gray-200 shadow-sm"
    >
      {({ open }) => (
        <>
          <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between">
              {/* left: burger (mobile) + logo + desktop nav */}
              <div className="flex items-center">
                <div className="flex items-center sm:hidden">
                  <Disclosure.Button className="inline-flex items-center justify-center rounded-md p-2 text-fairblue-500 hover:bg-fairgray focus:outline-none focus:ring-2 focus:ring-fairblue-500">
                    <span className="sr-only">
                      <EasyTrans>Navigation.SrMenuOpen</EasyTrans>
                    </span>
                    {open ? (
                      <XIcon className="block h-6 w-6" aria-hidden="true" />
                    ) : (
                      <MenuIcon className="block h-6 w-6" aria-hidden="true" />
                    )}
                  </Disclosure.Button>
                </div>

                <NavLink to="/" className="flex-shrink-0 ml-2 sm:ml-0">
                  <img
                    className="h-9 w-auto"
                    src={fairkomlogo}
                    alt="fairregister"
                  />
                </NavLink>

                <div className="hidden sm:ml-8 sm:flex sm:space-x-1">
                  {NAV_ITEMS.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className={navLinkClass}
                    >
                      <EasyTrans>{item.label}</EasyTrans>
                    </NavLink>
                  ))}
                </div>
              </div>

              {/* right: user menu / login */}
              <div className="flex items-center">
                {UserService.isLoggedIn() ? (
                  <Menu as="div" className="relative">
                    <Menu.Button className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 hover:bg-fairgray focus:outline-none focus:ring-2 focus:ring-fairblue-500">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-fairblue-500 text-sm font-semibold text-white">
                        {initials(UserService.getDisplayName())}
                      </span>
                      <span className="hidden text-sm font-medium text-gray-700 sm:block">
                        {UserService.getDisplayName()}
                      </span>
                      <ChevronDownIcon
                        className="h-4 w-4 text-gray-400"
                        aria-hidden="true"
                      />
                    </Menu.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-50 mt-2 w-48 origin-top-right rounded-xl bg-white py-1 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                        <Menu.Item>
                          {({ active }) => (
                            <NavLink
                              to="/profil"
                              className={classNames(
                                active ? "bg-fairgray" : "",
                                "block px-4 py-2 text-sm text-gray-700",
                              )}
                            >
                              <EasyTrans>Navigation.Profil</EasyTrans>
                            </NavLink>
                          )}
                        </Menu.Item>
                        <div className="my-1 border-t border-gray-100" />
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              type="button"
                              onClick={() => UserService.doLogout()}
                              className={classNames(
                                active ? "bg-fairgray" : "",
                                "block w-full px-4 py-2 text-left text-sm text-gray-700",
                              )}
                            >
                              <EasyTrans>Navigation.Logout</EasyTrans>
                            </button>
                          )}
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                ) : (
                  <Button
                    variant="cta"
                    size="sm"
                    onClick={() => UserService.doLogin()}
                  >
                    <EasyTrans>Navigation.Login</EasyTrans>
                  </Button>
                )}
              </div>
            </div>
          </div>

          <Disclosure.Panel className="sm:hidden border-t border-gray-200">
            <div className="space-y-1 px-2 pb-3 pt-2">
              {NAV_ITEMS.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.end}>
                  {(state) => (
                    <Disclosure.Button as="div" className={mobileLinkClass(state)}>
                      <EasyTrans>{item.label}</EasyTrans>
                    </Disclosure.Button>
                  )}
                </NavLink>
              ))}
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
};

export default Navbar;
